import React, { useEffect, useMemo, useRef, useState } from "react"
import TableContainer from "components/Common/TableContainer"
import { formatAmount, getTableSortingHeaderIcon } from "helpers/common"
import { useFormik } from "formik"
import * as Yup from "yup"
import PaymentUtrModel from "modals/paymentUTRModel"
import toast from "react-hot-toast"
import {
  useLazyPendingIcdPaymentsQuery,
  usePayPendingIcdPaymentsMutation,
} from "store/api/icds/icds.api"
import DefaultAvatar from "components/CommonForBoth/TopbarDropdown/DefaultAvatar"
import IcdPaymentsParts from "modals/IcdPaymentsParts"
const defaultFilters = {
  search: "",
  page: 1,
  pageSize: 20,
  timestamp: "",
}

const PaymentPage = ({ HeaderComponent }) => {
  const [getResults, { isFetching }] = useLazyPendingIcdPaymentsQuery()
  const [filters, setFilters] = useState(defaultFilters)
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const [isPayment, setIsPayment] = useState(false)
  const [selectedIcd, setSelectedIcd] = useState(null)
  const [mutate, { isLoading }] = usePayPendingIcdPaymentsMutation()
  const [paymentsModel, setPaymentsModel] = useState(false)
  const paymentsRef = useRef(null)

  //meta title
  document.title = "Admin Payments - Prachay"

  const getList = async () => {
    try {
      const { data } = await getResults(filters).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  useEffect(() => {
    getList()
  }, [filters])

  const handlePayment = async (values = null) => {
    try {
      const { data, success, message } = await mutate({
        icdId: selectedIcd?.id,
        paymentType: "OFFLINE",
        ...values,
        returnUrl: window.location.href,
      }).unwrap()
      if (success) {
        toast.success(message, { id: "toastMessage" })
        window.location.reload()
      }
    } catch (error) {
      const data = error?.data
      if (data?.errors) {
        const { errors } = data
        paymentValidation.setErrors(errors)
      }
      toast.error(error?.data?.message, { id: "toastMessage" })
    }
  }

  const paymentValidation = useFormik({
    enableReinitialize: true,
    initialValues: { itr: "", amount: "" },
    validationSchema: Yup.object({}),
    onSubmit: async (values) => {
      await handlePayment({ ...values })
    },
  })

  const columns = useMemo(() => [
    {
      header: ({ column }) => getTableSortingHeaderIcon(column, "Request ID"),
      accessorKey: "unique_code",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div className="code-number">{row?.original?.unique_code}</div>
      ),
    },
    {
      header: ({ column }) =>
        getTableSortingHeaderIcon(column, "Date of Payment"),
      accessorKey: "date_of_payment",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div>
          {row?.original?.investorsPayments.length > 0
            ? row?.original?.investorsPayments[0].formatted_created_at
            : "Not Applicable"}
        </div>
      ),
    },
    {
      header: ({ column }) => getTableSortingHeaderIcon(column, "Name"),
      accessorKey: "name",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <DefaultAvatar
          avatar_url={row?.original.investor?.avatar_url}
          email={row?.original.investor?.email}
          name={row?.original.investor?.name}
        />
      ),
    },
    {
      header: ({ column }) => getTableSortingHeaderIcon(column, "Amount (₹)"),
      accessorKey: "amount",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => <div>₹ {formatAmount(row?.original?.amount)}</div>,
    },
    {
      header: ({ column }) =>
        getTableSortingHeaderIcon(column, "Remaining Payment (₹)"),
      accessorKey: "remaining_amount",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <div>
          ₹{" "}
          {formatAmount(
            row?.original?.amount -
              (row?.original?.investorsPayments?.reduce(
                (sum, item) => sum + (item.amount || 0),
                0
              ) || 0)
          )}
        </div>
      ),
    },
    {
      header: ({ column }) => getTableSortingHeaderIcon(column, "Payment Mode"),
      accessorKey: "payment_mode",
      enableColumnFilter: false,
      enableSorting: true,
      cell: ({ row }) => (
        <>
          {["ONGOING", "REDEEMED"].includes(row?.original?.status) &&
          row?.original?.investorsPayments.length > 0
            ? row?.original?.investorsPayments[0].payment_type
            : "Not Applicable"}
        </>
      ),
    },
    {
      header: () => <div className="flex items-center gap-2">Status</div>,
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }) => (
        <div className="code-status">
          {["TERM_SHEET_FINALISED"].includes(row?.original?.status) && (
            <div className={`badge under_review`}>
              <span></span> Pending
            </div>
          )}
          {["FUNDS_IN_PROCESS"].includes(row?.original?.status) && (
            <div className={`badge link_expired`}>
              <span></span> Processing
            </div>
          )}
          {["ONGOING", "REDEEMED"].includes(row?.original?.status) && (
            <div className={`badge approved`}>
              <span></span> Paid
            </div>
          )}
        </div>
      ),
    },
    {
      id: "action",
      header: "Actions",
      cell: ({ row }) => (
        <div className="d-flex justify-content-center align-items-center gap-3">
          {["TERM_SHEET_FINALISED"].includes(row?.original?.status) && (
            <>
              <button
                onClick={() => {
                  setIsPayment(true)
                  setSelectedIcd(row?.original)
                  paymentValidation.setFieldValue(
                    "amount",
                    row?.original?.amount -
                      (row?.original?.investorsPayments?.reduce(
                        (sum, item) => sum + (item.amount || 0),
                        0
                      ) || 0)
                  )
                }}
                type="button"
                className="btn btn-cancel w-md"
              >
                Pay Now
              </button>
            </>
          )}
          {["ONGOING", "REDEEMED", "FUNDS_IN_PROCESS"].includes(
            row?.original?.status
          ) && (
            <>
              <button
                onClick={() => {
                  paymentsRef.current = row?.original?.investorsPayments
                  setPaymentsModel(true)
                }}
                type="button"
                className="btn btn-cancel w-md"
              >
                View Payments
              </button>
            </>
          )}
        </div>
      ),
    },
  ])

  return (
    <React.Fragment>
      <TableContainer
        isLoading={isFetching}
        count={count}
        filters={filters}
        setFilters={setFilters}
        HeaderComponent={HeaderComponent}
        columns={columns}
        data={data}
        isGlobalFilter={true}
        isCustomPageSize={true}
        // isGlobalFilter={true}
        isPagination={true}
        pagination="pagination"
        paginationWrapper="dataTables_paginate paging_simple_numbers"
        tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
      />
      <PaymentUtrModel
        icd={selectedIcd}
        open={isPayment}
        setOpen={setIsPayment}
        validation={paymentValidation}
        isLoading={isLoading}
      />

      {paymentsModel && paymentsRef?.current && (
        <IcdPaymentsParts
          open={paymentsModel}
          close={() => {
            setPaymentsModel(false)
            paymentsRef.current = null
          }}
          data={paymentsRef.current}
        />
      )}
    </React.Fragment>
  )
}

export default PaymentPage
